'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { slaMonitorListMachine, type TrackedInteraction } from './machines/slaMonitorListMachine'
import type { SlaEvent } from '../../../entities/sla/model/types'
import type { TriggeredEscalation } from '../../../entities/sla/model/escalation-engine'
import type { Interaction } from '../../../entities/interaction/model/types'

type SlaMonitorMachineApi = ReturnType<typeof useMachine<typeof slaMonitorListMachine>>

const SlaMonitorListMachineContext = createContext<SlaMonitorMachineApi | null>(null)

export function SlaMonitorListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(slaMonitorListMachine)
  return (
    <SlaMonitorListMachineContext.Provider value={api}>
      {children}
    </SlaMonitorListMachineContext.Provider>
  )
}

export function useSlaMonitorListMachine() {
  const ctx = useContext(SlaMonitorListMachineContext)
  if (!ctx) {
    throw new Error('useSlaMonitorListMachine must be used within SlaMonitorListMachineProvider')
  }
  return ctx
}

export function useSlaMonitorListStoreCompat() {
  const machineApi = useSlaMonitorListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      trackedInteractions: ctx.trackedInteractions,
      warnings: ctx.warnings,
      breaches: ctx.breaches,
      escalations: ctx.escalations,
      enableWarningNotifications: ctx.enableWarningNotifications,
      enableBreachNotifications: ctx.enableBreachNotifications,
      enableEscalationNotifications: ctx.enableEscalationNotifications,
      warningThresholdMinutes: ctx.warningThresholdMinutes,
      isRunning: ctx.isRunning,
      lastCheckTime: ctx.lastCheckTime,
      trackInteraction: (interaction: Interaction, policyId: string, queueId: string) =>
        send({
          type: 'TRACK_INTERACTION',
          interactionId: interaction.id,
          policyId,
          queueId,
          createdAt: new Date(interaction.createdAt),
          firstResponseAt: interaction.firstResponseAt
            ? new Date(interaction.firstResponseAt)
            : undefined,
          resolvedAt: interaction.resolvedAt ? new Date(interaction.resolvedAt) : undefined,
        }),
      untrackInteraction: (interactionId: string) =>
        send({ type: 'UNTRACK_INTERACTION', interactionId }),
      updateInteractionStatus: (interactionId: string, updates: Partial<TrackedInteraction>) =>
        send({ type: 'UPDATE_INTERACTION_STATUS', interactionId, updates }),
      pauseTracking: (interactionId: string) => send({ type: 'PAUSE_TRACKING', interactionId }),
      resumeTracking: (interactionId: string) => send({ type: 'RESUME_TRACKING', interactionId }),
      addWarning: (event: SlaEvent) => send({ type: 'ADD_WARNING', event }),
      addBreach: (event: SlaEvent) => send({ type: 'ADD_BREACH', event }),
      addEscalation: (escalation: TriggeredEscalation) =>
        send({ type: 'ADD_ESCALATION', escalation }),
      clearEvents: () => send({ type: 'CLEAR_EVENTS' }),
      setWarningNotifications: (enabled: boolean) =>
        send({ type: 'SET_WARNING_NOTIFICATIONS', enabled }),
      setBreachNotifications: (enabled: boolean) =>
        send({ type: 'SET_BREACH_NOTIFICATIONS', enabled }),
      setEscalationNotifications: (enabled: boolean) =>
        send({ type: 'SET_ESCALATION_NOTIFICATIONS', enabled }),
      setWarningThreshold: (minutes: number) => send({ type: 'SET_WARNING_THRESHOLD', minutes }),
      startMonitoring: () => send({ type: 'START_MONITORING' }),
      stopMonitoring: () => send({ type: 'STOP_MONITORING' }),
      setLastCheckTime: (time: Date) => send({ type: 'SET_LAST_CHECK_TIME', time }),
    }
  }, [machineApi])
}

export function useSlaMonitorActions() {
  const machineApi = useSlaMonitorListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    trackInteraction: (interaction: Interaction, policyId: string, queueId: string) =>
      send({
        type: 'TRACK_INTERACTION',
        interactionId: interaction.id,
        policyId,
        queueId,
        createdAt: new Date(interaction.createdAt),
        firstResponseAt: interaction.firstResponseAt
          ? new Date(interaction.firstResponseAt)
          : undefined,
        resolvedAt: interaction.resolvedAt ? new Date(interaction.resolvedAt) : undefined,
      }),
    untrackInteraction: (interactionId: string) =>
      send({ type: 'UNTRACK_INTERACTION', interactionId }),
    updateInteractionStatus: (interactionId: string, updates: Partial<TrackedInteraction>) =>
      send({ type: 'UPDATE_INTERACTION_STATUS', interactionId, updates }),
    pauseTracking: (interactionId: string) => send({ type: 'PAUSE_TRACKING', interactionId }),
    resumeTracking: (interactionId: string) => send({ type: 'RESUME_TRACKING', interactionId }),
    addWarning: (event: SlaEvent) => send({ type: 'ADD_WARNING', event }),
    addBreach: (event: SlaEvent) => send({ type: 'ADD_BREACH', event }),
    addEscalation: (escalation: TriggeredEscalation) =>
      send({ type: 'ADD_ESCALATION', escalation }),
    clearEvents: () => send({ type: 'CLEAR_EVENTS' }),
    setWarningNotifications: (enabled: boolean) =>
      send({ type: 'SET_WARNING_NOTIFICATIONS', enabled }),
    setBreachNotifications: (enabled: boolean) =>
      send({ type: 'SET_BREACH_NOTIFICATIONS', enabled }),
    setEscalationNotifications: (enabled: boolean) =>
      send({ type: 'SET_ESCALATION_NOTIFICATIONS', enabled }),
    setWarningThreshold: (minutes: number) => send({ type: 'SET_WARNING_THRESHOLD', minutes }),
    startMonitoring: () => send({ type: 'START_MONITORING' }),
    stopMonitoring: () => send({ type: 'STOP_MONITORING' }),
    setLastCheckTime: (time: Date) => send({ type: 'SET_LAST_CHECK_TIME', time }),
  }), [send])
}

export function useSlaTrackedInteractions() {
  const compat = useSlaMonitorListStoreCompat()
  return Array.from(compat.trackedInteractions.values())
}

export function useSlaWarnings() {
  const compat = useSlaMonitorListStoreCompat()
  return compat.warnings
}

export function useSlaBreaches() {
  const compat = useSlaMonitorListStoreCompat()
  return compat.breaches
}

export function useSlaEscalations() {
  const compat = useSlaMonitorListStoreCompat()
  return compat.escalations
}

export function useSlaMonitorSettings() {
  const compat = useSlaMonitorListStoreCompat()
  return {
    enableWarningNotifications: compat.enableWarningNotifications,
    enableBreachNotifications: compat.enableBreachNotifications,
    enableEscalationNotifications: compat.enableEscalationNotifications,
    warningThresholdMinutes: compat.warningThresholdMinutes,
  }
}
